import React, { useEffect, useRef, useState } from 'react';
import { MousePointer2, Sparkles, Zap } from 'lucide-react';

interface InteractiveElementsProps {
  enableCursor?: boolean;
  enableParticles?: boolean;
  enableHoverEffects?: boolean;
  className?: string;
}

export const InteractiveElements: React.FC<InteractiveElementsProps> = ({
  enableCursor = true,
  enableParticles = true,
  enableHoverEffects = true,
  className = ''
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isClicking, setIsClicking] = useState(false);
  const [particles, setParticles] = useState<Array<{
    id: number;
    x: number;
    y: number;
    life: number;
  }>>([]);
  const particleIdRef = useRef(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseDown = () => {
      setIsClicking(true);
      
      if (enableParticles) {
        // Create particles on click
        const newParticles = Array.from({ length: 5 }, () => ({
          id: particleIdRef.current++,
          x: mousePosition.x + (Math.random() - 0.5) * 20,
          y: mousePosition.y + (Math.random() - 0.5) * 20,
          life: 1
        }));
        
        setParticles(prev => [...prev, ...newParticles]);
      }
    };

    const handleMouseUp = () => {
      setIsClicking(false);
    };

    if (enableCursor || enableParticles) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [enableCursor, enableParticles, mousePosition.x, mousePosition.y]);

  // Particle animation
  useEffect(() => {
    if (!enableParticles) return;

    const interval = setInterval(() => {
      setParticles(prev => 
        prev
          .map(particle => ({
            ...particle,
            life: particle.life - 0.02,
            y: particle.y - 1
          }))
          .filter(particle => particle.life > 0)
      );
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [enableParticles]);

  // Add interactive hover effects to elements
  useEffect(() => {
    if (!enableHoverEffects) return;

    const addHoverEffects = () => {
      const interactiveElements = document.querySelectorAll(
        'button, a, [role="button"], .card-interactive, .hover-lift'
      );

      const handleMouseEnter = (e: Event) => {
        const element = e.target as HTMLElement;
        element.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        element.style.transform = 'translateY(-2px) scale(1.02)';
        element.style.boxShadow = '0 8px 25px -5px rgba(0, 0, 0, 0.15)';
        
        // Add glow effect
        element.style.filter = 'brightness(1.05)';
      };

      const handleMouseLeave = (e: Event) => {
        const element = e.target as HTMLElement;
        element.style.transform = 'translateY(0) scale(1)';
        element.style.boxShadow = '';
        element.style.filter = '';
      };

      interactiveElements.forEach(element => {
        element.addEventListener('mouseenter', handleMouseEnter);
        element.addEventListener('mouseleave', handleMouseLeave);
      });

      return () => {
        interactiveElements.forEach(element => {
          element.removeEventListener('mouseenter', handleMouseEnter);
          element.removeEventListener('mouseleave', handleMouseLeave);
        });
      };
    };

    const cleanup = addHoverEffects();
    
    // Re-apply effects when new elements are added
    const observer = new MutationObserver(addHoverEffects);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      cleanup?.();
      observer.disconnect();
    };
  }, [enableHoverEffects]);

  return (
    <div className={`fixed inset-0 pointer-events-none z-40 ${className}`}>
      {/* Custom Cursor */}
      {enableCursor && (
        <>
          <div
            className="absolute w-4 h-4 bg-orange-primary rounded-full mix-blend-difference transition-transform duration-150 ease-out"
            style={{
              left: mousePosition.x - 8,
              top: mousePosition.y - 8,
              transform: isClicking ? 'scale(1.5)' : 'scale(1)',
              opacity: 0.8
            }}
          />
          
          <div
            className="absolute w-8 h-8 border-2 border-orange-primary/50 rounded-full transition-all duration-300 ease-out"
            style={{
              left: mousePosition.x - 16,
              top: mousePosition.y - 16,
              transform: isClicking ? 'scale(0.8)' : 'scale(1)',
              opacity: 0.6
            }}
          />
        </>
      )}

      {/* Click Particles */}
      {enableParticles && particles.map(particle => (
        <div
          key={particle.id}
          className="absolute w-1 h-1 bg-orange-primary rounded-full"
          style={{
            left: particle.x,
            top: particle.y,
            opacity: particle.life,
            transform: `scale(${particle.life})`,
            transition: 'all 0.1s ease-out'
          }}
        />
      ))}

      {/* Magnetic cursor effect for special elements */}
      <MagneticCursor />
    </div>
  );
};

// Magnetic cursor effect for special elements
const MagneticCursor: React.FC = () => {
  const [magneticEffect, setMagneticEffect] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const magneticElements = document.querySelectorAll('[data-magnetic]');
      
      magneticElements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        const distance = Math.sqrt(
          Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2)
        );
        
        if (distance < 100) {
          const force = (100 - distance) / 100;
          const angleX = (e.clientX - centerX) * force * 0.3;
          const angleY = (e.clientY - centerY) * force * 0.3;
          
          (element as HTMLElement).style.transform = 
            `translate(${angleX}px, ${angleY}px) scale(${1 + force * 0.1})`;
        } else {
          (element as HTMLElement).style.transform = 'translate(0, 0) scale(1)';
        }
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return null;
};

// Component for ripple effects
export const RippleEffect: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
}> = ({ children, className = '' }) => {
  const [ripples, setRipples] = useState<Array<{
    id: number;
    x: number;
    y: number;
    size: number;
  }>>([]);
  const rippleIdRef = useRef(0);

  const createRipple = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const size = Math.max(rect.width, rect.height);
    
    const newRipple = {
      id: rippleIdRef.current++,
      x,
      y,
      size
    };
    
    setRipples(prev => [...prev, newRipple]);
    
    setTimeout(() => {
      setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
    }, 600);
  };

  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      onMouseDown={createRipple}
    >
      {children}
      
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="absolute bg-white/20 rounded-full animate-ping pointer-events-none"
          style={{
            left: ripple.x - ripple.size / 2,
            top: ripple.y - ripple.size / 2,
            width: ripple.size,
            height: ripple.size,
            animationDuration: '0.6s'
          }}
        />
      ))}
    </div>
  );
};